import { Injectable, Signal, WritableSignal, computed, effect, signal, untracked } from '@angular/core';
import { Subject } from 'rxjs';
import { NavigationAction } from 'src/app/interfaces/navigation-action';
import { View } from 'src/app/interfaces/view';
import { ViewTitleButton } from 'src/app/interfaces/view-title-button';

@Injectable({
  providedIn: 'root'
})
export class Navigation2Service {
  views = signal<View[]>([]);
  siblingViews: View[] = [];
  activeViewIndexAlwaysriggers = signal<number | null>(null, { equal: () => false });
  activeViewIndex = signal<number | null>(null);
  activeView = computed(() => this.views()[this.activeViewIndex() ?? 0] ?? null);
  canNavigateBack = computed(() => this.views().slice().reverse().slice(this.views().length - (this.activeViewIndex() ?? 0)).filter(v => !v.isHidden).length > 0);
  canNavigateNext = computed(() => this.views().slice((this.activeViewIndex() ?? 0) + 1).filter(v => !v.isHidden).length > 0);
  overrideTitle = signal<string | null>(null);
  globalTitleButtons = signal<ViewTitleButton[]>([]);
  titleButtons = signal<ViewTitleButton[]>([]);
  shouldShowNavBar = signal(true);
  subtitle = signal<string | null>(null);

  private subject = new Subject<NavigationAction>();
  private activeViewIndexChange = new Subject<number | null>();
  constructor() { }

  present(view: View) {
    const index = this.siblingViews.length;
    this.siblingViews.push(view);
    this.subject.next({ action: 'Present', data: view });
    this.shouldShowNavBar.set(false);
    return index;
  }

  dismiss(siblingViewIndex: number) {
    const viewToRemove = this.siblingViews[siblingViewIndex];
    const beforeIndex = this.siblingViews.slice(0, siblingViewIndex);
    const afterIndex = this.siblingViews.slice(siblingViewIndex + 1, this.siblingViews.length);
    this.siblingViews = beforeIndex.concat(afterIndex);
    this.subject.next({ action: 'Dismiss', data: viewToRemove });
    if (this.siblingViews.length === 0) {
      this.shouldShowNavBar.set(true);
    }
  }

  dismissAll() {
    const viewsToRemove = this.siblingViews.slice();
    viewsToRemove.forEach(() => this.dismiss(0));
    this.subject.next({ action: 'DismissAll', data: viewsToRemove });
    this.shouldShowNavBar.set(true);
  }

  next() {
    const activeIndex = this.activeViewIndex() ?? 0;
    const numberOfIndexesToNext = this.getNumberOfIndexesToNextVisibleView(this.views(), activeIndex);
    if (numberOfIndexesToNext != null) {
      const newIndex = numberOfIndexesToNext + activeIndex;
      this.navigateTo(newIndex);
    }
  }

  back() {
    const activeIndex = this.activeViewIndex() ?? 0;
    const numberOfIndexesToNext = this.getNumberOfIndexesToNextVisibleView(this.views().slice().reverse(), Math.max(this.views().length - activeIndex - 1, 0));
    if (numberOfIndexesToNext != null) {
      const newIndex = activeIndex - numberOfIndexesToNext;
      this.navigateTo(newIndex);
    }
  }

  setViewIsHidden(index: number, isHidden: boolean) {
    this.mutateViews((views) => {
      views[index].isHidden = isHidden;
    })
  }

  setViewdoesNextPrevSkip(index: number, doesNextPrevSkip: boolean) {
    this.mutateViews((views) => {
      views[index].doesNextPrevSkip = doesNextPrevSkip;
    })
  }

  mutateViews(mutateViews: (views: View[]) => void) {
    const views = untracked(() => this.views()).slice();
    mutateViews(views);
    this.views.set(views);
  }

  private getNumberOfIndexesToNextVisibleView(views: View[], activeIndex: number) {
    const nextViews = views.slice(activeIndex + 1);
    const nextNonHiddenViewIndex = nextViews.findIndex(v => !v.isHidden && !v.doesNextPrevSkip);
    if (nextNonHiddenViewIndex != -1) {
      const newIndex = nextNonHiddenViewIndex + 1;
      return newIndex;
    }
    return null;
  }

  navigateTo(index: number) {
    if (this.siblingViews.length > 0) {
      this.dismissAll();
    }
    this.titleButtons.set([]);
    this.activeViewIndex.set(index);
    this.activeViewIndexAlwaysriggers.set(index);
    // this.activeViewIndex = index;
    // this.activeView = this.views()[this.activeViewIndex ?? 0];
    // this.canNavigateBack.set((this.activeViewIndex ?? 0) > 0);
    // this.canNavigateNext.set((this.activeViewIndex ?? 0) < this.views.length - 1);
    // this.activeViewIndexChange.next(this.activeViewIndex);
  }

  subscribe(next?: (value: NavigationAction) => void) {
    return this.subject.subscribe({ next: next });
  }
}
